import { Autocomplete, Grid, Skeleton, TextField } from '@mui/material';
import React from 'react';
import InputForm from '../../../commun/input-form';
import { ReferenceDto } from '../../../../services/dtos/reference.dto';
import { AddressGouvFeaturesPropertiesFormDto } from '../../../../services/dtos/form/address-gouv-features-properties-form.dto';
import { parseHouseNumber } from '../../../../utils/address-parser.utils';
import { useFormikContext } from 'formik';

/**
 * Composant formulaire d'adresse standardisé
 * @constructor
 */
export default function AddressForm(props: {
    handleChangeCountry: (e: React.SyntheticEvent, v: ReferenceDto | null) => void;
    name: string | undefined;
    countriesSelection: ReferenceDto[];
    selectedCountry?: ReferenceDto;
    handleSearchAddress?: (event: object, value: string, reason: string) => void;
    addressesGouv?: AddressGouvFeaturesPropertiesFormDto[];
    handleSelectAddress?: (address: AddressGouvFeaturesPropertiesFormDto | null) => void;
    setFieldValue?: (field: string, value: any) => void;
}) {
    // Utilisation du contexte Formik pour mettre à jour les valeurs
    const { setFieldValue } = useFormikContext();

    // Création de la page
    return (
        <Grid container spacing={1}>
            {/*Autocomplete pour recherche d'adresse via API data.gouv (si fourni)*/}
            {props.handleSearchAddress && props.addressesGouv && (
                <Grid item xs={12}>
                    <Autocomplete
                        data-testid={props.name + 'autocomplete-address'}
                        disablePortal
                        id={props.name + 'address'}
                        autoHighlight
                        options={props.addressesGouv}
                        getOptionLabel={option => option.label}
                        onInputChange={props.handleSearchAddress}
                        onChange={(_, value) => {
                            if (value && props.setFieldValue) {
                                // Parser le numéro de rue pour séparer le numéro des bis/ter
                                const parsedNumber = parseHouseNumber(value.housenumber || '');

                                // Remplir automatiquement les champs du formulaire
                                props.setFieldValue(props.name + 'numeroRue', parsedNumber.numero);
                                props.setFieldValue(props.name + 'complement.numeroRue', parsedNumber.complement);
                                props.setFieldValue(props.name + 'rue', value.street || value.name || '');
                                props.setFieldValue(props.name + 'ville', value.city || '');
                                props.setFieldValue(props.name + 'codePostal', value.postcode || '');
                                props.handleSelectAddress?.(value);
                            } else {
                                props.handleSelectAddress?.(null);
                            }
                        }}
                        renderInput={params => (
                            <TextField
                                {...params}
                                label="Rechercher votre adresse (optionnel)"
                                helperText="Tapez votre adresse pour remplir automatiquement les champs ci-dessous"
                                sx={{ marginBottom: '20px' }}
                            />
                        )}
                    />
                </Grid>
            )}

            <Grid item xs={12} sm={2}>
                {/*Champ numero de rue*/}
                <InputForm
                    id={props.name + 'numeroRue'}
                    data-testid={props.name + 'numeroRue'}
                    name={props.name + 'numeroRue'}
                    required
                    label={'N°'}
                ></InputForm>
            </Grid>
            <Grid item xs={12} sm={2}>
                {/*Champ complement numero de rue*/}
                <InputForm
                    id={props.name + 'complementnumeroRue'}
                    data-testid={props.name + 'complementnumeroRue'}
                    name={props.name + 'complement.numeroRue'}
                    label={'Bis, Ter, ...'}
                ></InputForm>
            </Grid>
            <Grid item xs={12} sm={8}>
                {/*Champ nom de rue*/}
                <InputForm
                    id={props.name + 'rue'}
                    data-testid={props.name + 'rue'}
                    name={props.name + 'rue'}
                    required
                    label={'Rue'}
                ></InputForm>
            </Grid>
            <Grid item xs={12} sm={12}>
                {/*Champ complément de rue*/}
                <InputForm
                    id={props.name + 'complementRue'}
                    data-testid={props.name + 'complementRue'}
                    name={props.name + 'complement.rue'}
                    label={'Complément de rue'}
                ></InputForm>
            </Grid>
            <Grid item xs={12} sm={6}>
                {/*Champ entree ou residence...*/}
                <InputForm
                    id={props.name + 'batiment'}
                    data-testid={props.name + 'batiment'}
                    name={props.name + 'complement.batiment'}
                    label={'Entrée, résidence...'}
                ></InputForm>
            </Grid>
            <Grid item xs={12} sm={6}>
                {/*Champ étage*/}
                <InputForm
                    id={props.name + 'etage'}
                    data-testid={props.name + 'etage'}
                    name={props.name + 'complement.etage'}
                    label={'Etage'}
                ></InputForm>
            </Grid>
            <Grid item xs={12} sm={6}>
                {/*Champ appartement*/}
                <InputForm
                    id={props.name + 'appartement'}
                    data-testid={props.name + 'appartement'}
                    name={props.name + 'complement.appartement'}
                    label={'Appartement'}
                ></InputForm>
            </Grid>
            {/*Champ complement d'adresse*/}
            <Grid item xs={12} sm={6}>
                <InputForm
                    id={props.name + 'escalier'}
                    data-testid={props.name + 'escalier'}
                    name={props.name + 'complement.escalier'}
                    label={'Escalier'}
                ></InputForm>
            </Grid>
            {/*Champ code postal*/}
            <Grid item xs={12} sm={4}>
                <InputForm
                    id={props.name + 'codePostal'}
                    data-testid={props.name + 'codePostal'}
                    name={props.name + 'codePostal'}
                    required
                    label={'Code postal'}
                ></InputForm>
            </Grid>
            {/*Champ ville*/}
            <Grid item xs={12} sm={4}>
                <InputForm
                    id={props.name + 'ville'}
                    data-testid={props.name + 'ville'}
                    name={props.name + 'ville'}
                    required
                    label={'Ville'}
                ></InputForm>
            </Grid>
            {/*Champ complement ville*/}
            <Grid item xs={12} sm={4}>
                <InputForm
                    id={props.name + 'complementville'}
                    data-testid={props.name + 'complementville'}
                    name={props.name + 'complement.ville'}
                    label={'Complément ville'}
                ></InputForm>
            </Grid>
            {/*Champ boite postale*/}
            <Grid item xs={12} sm={4}>
                <InputForm
                    id={props.name + 'boitePostale'}
                    data-testid={props.name + 'boitePostale'}
                    name={props.name + 'boitePostale'}
                    label={'Boite postale'}
                ></InputForm>
            </Grid>
            {/*Champ pays*/}
            {props.countriesSelection ? (
                <Grid item xs={12} sm={4}>
                    {/*autoCompoete*/}
                    <Autocomplete
                        data-testid={props.name + 'autocomplete-pays'}
                        disablePortal
                        id={props.name + 'pays'}
                        autoHighlight
                        options={props.countriesSelection}
                        getOptionLabel={option => option.value}
                        value={props.selectedCountry}
                        isOptionEqualToValue={(option, value) => option.value === value.value}
                        onChange={(event, value) => {
                            // Appeler le handler du parent
                            props.handleChangeCountry(event, value);
                            // Mettre à jour le champ Formik
                            if (setFieldValue) {
                                setFieldValue(props.name + 'pays', value);
                            }
                        }}
                        renderInput={params => (
                            // TextField pour pays
                            <TextField {...params} name={props.name + 'pays'} label="Pays" required />
                        )}
                    ></Autocomplete>
                </Grid>
            ) : (
                // skeleton
                <Skeleton variant="rectangular" height={100}></Skeleton>
            )}
        </Grid>
    );
}
